import clsx from 'clsx';
import { map } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';
import { useWindowSize } from 'usehooks-ts';
import {
  useBankTxsCsv,
  useBillTxsCsv,
  useBuyCryptoTxsCsv,
  useMerchantPaymentsCsv,
} from '@/api/queryHooks/useTransactionsController';
import { Routes } from '@/constants/routes';
import { removeTrailingSlash } from '@/utils/functions';
import useIsAUSResident from '@/hooks/useIsAUSResident';
import { Button, Icon, PopoverWithTrigger, SelectList, SelectListItem } from '../../../../../components/common';
import styles from './TransactionsPageTemplateControls.module.css';

interface TransactionsPageTemplateControlsProps {
  tabs: { link: string; name: string }[];
  role?: 'business' | 'personal';
}

export const TransactionsPageTemplateControls = ({
  tabs,
  role = 'business',
}: TransactionsPageTemplateControlsProps) => {
  const router = useRouter();
  const { width } = useWindowSize();
  const isTBankTab = `/${router.asPath.split('/').at(-1)}` === Routes.TRANSACTIONS;
  const isTBillPaymentTab = router.asPath.includes(Routes.T_BILL_PAYMENT);
  const isTBuyCryptoTab = router.asPath.includes(Routes.T_BUY_CRYPTO);
  const isMerchantPaymentsTab = router.asPath.includes(Routes.T_MERCHANT_PAYMENTS);
  const { data: bankTxsCsv, refetch: refetchBankCsv } = useBankTxsCsv({ enabled: false });
  const { data: billTxsCsv, refetch: refetchBillTxsCsv } = useBillTxsCsv({ enabled: false });
  const { data: buyCryptoTxsCsv, refetch: refetchBuyCryptoTxsCsv } = useBuyCryptoTxsCsv({
    enabled: false,
  });
  const { data: merchantPaymentsCsv, refetch: refetchMerchantPaymentsCsv } = useMerchantPaymentsCsv({
    enabled: false,
  });

  const { isAUSResident } = useIsAUSResident();

  const filteredTabs = useMemo(() => {
    return tabs.filter((tab) => {
      if (tab.link.includes(Routes.T_BUY_CRYPTO) && !isAUSResident) {
        return false;
      }
      return true;
    });
  }, [isAUSResident, tabs]);

  const isActive = useCallback(
    (role: 'business' | 'personal', href: string) => {
      const currentPath = router?.asPath || '';
      return removeTrailingSlash(currentPath) === removeTrailingSlash(href);
    },
    [router?.asPath],
  );

  function buildCsvLink() {
    let csv;
    switch (true) {
      case isTBankTab: {
        csv = bankTxsCsv;
        break;
      }
      case isTBillPaymentTab: {
        csv = billTxsCsv;
        break;
      }
      case isTBuyCryptoTab: {
        csv = buyCryptoTxsCsv;
        break;
      }
      case isMerchantPaymentsTab: {
        csv = merchantPaymentsCsv;
        break;
      }
      default:
        break;
    }
    return `data:text/csv;charset=utf-8,${csv}`;
  }

  const onPopoverPress = () => {
    switch (true) {
      case isTBankTab: {
        refetchBankCsv();
        break;
      }
      case isTBillPaymentTab: {
        refetchBillTxsCsv();
        break;
      }
      case isTBuyCryptoTab: {
        refetchBuyCryptoTxsCsv();
        break;
      }
      case isMerchantPaymentsTab: {
        refetchMerchantPaymentsCsv();
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className={styles.controls}>
      <div className={styles.head}>
        <h2 className={styles.title}>Transactions</h2>
        <PopoverWithTrigger
          className={styles.popover}
          renderTrigger={({ state, triggerProps, triggerRef }) => {
            return (
              <>
                {width > 1000 ? (
                  <Button
                    {...triggerProps}
                    iconPosition="right"
                    variants="secondary"
                    icon={state.isOpen ? 'chevronUp' : 'chevronDown'}
                    additionalBg="blue"
                    buttonRef={triggerRef}
                    onPress={() => {
                      onPopoverPress();
                      return state.open();
                    }}
                  >
                    Download Report
                  </Button>
                ) : (
                  <Button
                    {...triggerProps}
                    variants="secondary"
                    additionalBg="blue"
                    buttonRef={triggerRef}
                    onPress={() => {
                      onPopoverPress();
                      return state.open();
                    }}
                    className={styles.downloadButton}
                  >
                    <Icon name="download" />
                  </Button>
                )}
              </>
            );
          }}
        >
          <SelectList>
            <SelectListItem>
              <a className={styles.selectListLink} href={buildCsvLink()} download={`${Date.now()}.csv`}>
                Download All in CSV
              </a>
            </SelectListItem>
          </SelectList>
        </PopoverWithTrigger>
      </div>
      <ul className={styles.tabs}>
        {map(filteredTabs, ({ link, name }) => (
          <li
            className={clsx(styles.tabsItem, {
              [styles.active]: isActive(role, link),
            })}
            key={link}
          >
            <Link className={styles.tabsLink} href={link}>
              {name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
